import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    if (document.readyState === 'complete') {
      setTimeout(() => this.#selectInitialVariantMedia());
    } else {
      window.addEventListener('load', () => this.#selectInitialVariantMedia(), { once: true, signal });
    }
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  //   #handleVariantUpdate = (event) => {
  //   const source = event.detail.data.html;

  //   if (!source) return;
  //   const newMediaGallery = source.querySelector('media-gallery');

  //   if (!newMediaGallery) return;

  //   // PDP: No change needed here; perform a standard replace
  //   this.replaceWith(newMediaGallery);
  // };
  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;
   
   
    if (!variantResource || !this.slideshow) {
      console.log('🖼️ Media Gallery: Missing variant resource or slideshow');
      return;
    }

    // Get the featured media ID from the variant
    const featuredMediaId = variantResource.featured_media?.id;
    console.log('🖼️ Media Gallery: Featured media ID:', featuredMediaId);

    if (featuredMediaId) { 
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false }).then(() => this.#updateGalleryState());
    } else {
      console.log('🖼️ Media Gallery: No featured media ID found');
    }
  };

  #selectInitialVariantMedia = (attempt = 0) => {
    const target = this.closest('.shopify-section, dialog');
    const variantJson = target?.querySelector('variant-picker script[type="application/json"]')?.textContent;
    let featuredMediaId;

    if (variantJson) {
      try {
        featuredMediaId = JSON.parse(variantJson).featured_media?.id;
      } catch (_) {}
    }

    requestAnimationFrame(async () => {
      const slideshow = this.slideshow || this.querySelector('slideshow-component');
      const slide = slideshow?.refs?.slides?.find((item) => item.getAttribute('slide-id') == featuredMediaId);

      if (featuredMediaId && slideshow && slide) {
        await slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
        const selectedSlide = slideshow.refs?.slides?.[slideshow.current];
        if (selectedSlide?.getAttribute('slide-id') == featuredMediaId) {
          this.#updateGalleryState();
          return;
        }
      }

      if (attempt < 20 && this.isConnected) {
        setTimeout(() => this.#selectInitialVariantMedia(attempt + 1), 100);
        return;
      }

      this.#updateGalleryState();
    });
  };

  #handleSlideshowSelect = (event) => {
    this.#updateGalleryState(event.detail.slide);
  };

  #updateGalleryState = (slide) => {
    const state = this.querySelector('[data-zfg-gallery-state]');
    if (!state) return;
    const slides = this.slideshow?.refs?.slides || [];
    const activeSlide = slide || slides[this.slideshow?.current || 0];
    const description = activeSlide?.querySelector('[data-zfg-photo-description]')?.textContent?.trim() || '';
    const section = this.closest('.shopify-section, dialog');
    const colorFieldset = [...(section?.querySelectorAll('fieldset[data-option-name]') || [])].find((fieldset) => /colou?r/i.test(fieldset.dataset.optionName || ''));
    const selectedColor = colorFieldset?.querySelector('input:checked')?.value || '';
    const photoTarget = state.querySelector('[data-zfg-viewing-photo]');
    const colorTarget = state.querySelector('[data-zfg-selected-color]');
    if (photoTarget) photoTarget.textContent = description;
    if (colorTarget) colorTarget.textContent = selectedColor;
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
